import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { adminApi } from "../services/api";

const AVATAR_COLORS = ["#3a47ff", "#10b981", "#f59e0b", "#ec4899", "#8b5cf6", "#06b6d4", "#ef4444", "#0a1530"];
const avatarColor = (s) => AVATAR_COLORS[s.charCodeAt(0) % AVATAR_COLORS.length];
const initials = (n) => n.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();
const fmtDate = (iso) => new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });

const FILTERS = [
  { value: "all", label: "All" },
  { value: "active", label: "Active" },
  { value: "expired", label: "Expired" },
  { value: "none", label: "No connection" },
];

const formatStatus = (status) =>
  (status || "unknown")
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

const ConnectionBadge = ({ status, isExpired }) => {
  const label = isExpired && status === "active" ? "Expired" : formatStatus(status);
  const isActive = label.toLowerCase() === "active";

  return (
    <span
      className={`inline-flex items-center gap-1 py-0.5 px-2 rounded-full text-[11px] font-semibold ${
        isActive ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
      }`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${isActive ? "bg-green-600" : "bg-red-600"}`}></span>
      {label}
    </span>
  );
};

const UsersPage = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [expanded, setExpanded] = useState(() => new Set());
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeConnections: 0,
    expiredConnections: 0,
    notConnected: 0,
  });

  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, pagination.limit, query, filter]);

  const fetchStats = async () => {
    try {
      const response = await adminApi.getUserStats();
      setStats(response.data.stats);
    } catch (err) {
      console.error("Failed to load user stats", err);
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await adminApi.getUsers({
        page: pagination.page,
        limit: pagination.limit,
        search: query,
        status: filter !== "all" ? filter : undefined,
      });
      setUsers(response.data.users);
      setPagination((prev) => ({
        ...prev,
        ...response.data.pagination,
      }));
    } catch (err) {
      setError("Failed to load users");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleQueryChange = (value) => {
    setQuery(value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleFilterChange = (value) => {
    setFilter(value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const toggleExpanded = (userId) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(userId) ? next.delete(userId) : next.add(userId);
      return next;
    });
  };

  const handleUserClick = (email) => {
    navigate(`/audit-logs?userFilter=${encodeURIComponent(email)}`);
  };

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handlePageSizeChange = (newPageSize) => {
    setPagination((prev) => ({ ...prev, limit: newPageSize, page: 1 }));
  };

  const renderRows = () => {
    if (users.length === 0) {
      return (
        <tr>
          <td colSpan={4} className="py-20 px-4">
            <div className="flex flex-col items-center justify-center text-center">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="mb-4">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
              </svg>
              <h3 className="text-lg font-semibold text-ink mb-2">No Users Found</h3>
              <p className="text-sm text-muted max-w-md">
                {query || filter !== "all"
                  ? "No users match your current filters. Try adjusting your search criteria."
                  : "No users have been registered yet. Users will appear here once they connect to QuickBooks."}
              </p>
            </div>
          </td>
        </tr>
      );
    }

    return users.flatMap((u) => {
      const conns = u.connections || [];
      const isOpen = expanded.has(u._id);
      const first = conns[0];

      const userCell = (
        <td rowSpan={isOpen && conns.length > 1 ? conns.length : 1} className="py-2.5 px-4 border-b border-line align-top">
          <div className="flex items-center gap-2.5 cursor-pointer" onClick={() => handleUserClick(u.email)}>
            <div
              className="w-8 h-8 rounded-full grid place-items-center text-white font-semibold text-xs shrink-0"
              style={{ background: avatarColor(u.name) }}
            >
              {initials(u.name)}
            </div>
            <div className="min-w-0">
              <div className="font-semibold text-ink text-[13px] truncate">{u.name}</div>
              <div className="text-muted text-xs truncate">{u.email}</div>
            </div>
          </div>
        </td>
      );

      if (conns.length === 0) {
        return (
          <tr key={u._id}>
            {userCell}
            <td className="py-2.5 px-4 border-b border-line">
              <span className="text-muted text-xs">Not connected</span>
            </td>
            <td className="py-2.5 px-4 border-b border-line text-muted text-xs">—</td>
            <td className="py-2.5 px-4 border-b border-line text-muted text-xs whitespace-nowrap">{fmtDate(u.createdAt || u.updatedAt)}</td>
          </tr>
        );
      }

      const toggle =
        conns.length > 1 ? (
          <button
            onClick={() => toggleExpanded(u._id)}
            className="ml-2 py-0.5 px-2 text-[11px] font-medium border border-line rounded-md bg-white hover:bg-soft transition-colors"
          >
            {isOpen ? "Hide" : `${conns.length - 1} more`}
          </button>
        ) : null;

      const firstRow = (
        <tr key={u._id} className={isOpen && conns.length > 1 ? "" : "border-b border-line"}>
          {userCell}
          <td className={`py-2.5 px-4 ${isOpen && conns.length > 1 ? "" : "border-b border-line"}`}>
            <div className="text-xs text-ink truncate mb-1">{first.companyName || "—"}</div>
            <ConnectionBadge status={first.status} isExpired={first.isExpired} />
            {toggle}
          </td>
          <td className={`py-2.5 px-4 font-mono text-[11px] text-muted truncate ${isOpen && conns.length > 1 ? "" : "border-b border-line"}`}>
            {first.realmId || "—"}
          </td>
          <td className={`py-2.5 px-4 text-muted text-xs whitespace-nowrap ${isOpen && conns.length > 1 ? "" : "border-b border-line"}`}>
            {fmtDate(u.createdAt || u.updatedAt)}
          </td>
        </tr>
      );

      const restRows = isOpen
        ? conns.slice(1).map((c, i) => {
            const isLast = i === conns.length - 2;
            return (
              <tr key={`${u._id}-${i}`} className={`bg-soft ${isLast ? "border-b border-line" : ""}`}>
                <td className="py-2 px-4">
                  <div className="text-xs text-ink truncate mb-1">{c.companyName || "—"}</div>
                  <ConnectionBadge status={c.status} isExpired={c.isExpired} />
                </td>
                <td className="py-2 px-4 font-mono text-[11px] text-muted truncate">{c.realmId || "—"}</td>
                <td className="py-2 px-4"></td>
              </tr>
            );
          })
        : [];

      return [firstRow, ...restRows];
    });
  };

  return (
    <div className="px-6 py-8 pb-24 font-sans text-ink bg-white h-full">
      <div className="flex items-end justify-between gap-4 flex-wrap mb-8">
        <div>
          <h1 className="text-3xl font-bold tracking-tight m-0">Users</h1>
          <p className="text-muted text-[15px] mt-2 mb-0">Manage accounts connected to your QuickBooks OAuth Server.</p>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="border border-line rounded-xl p-5 bg-white">
          <div className="text-xs text-muted uppercase tracking-wider font-semibold">Total users</div>
          <div className="text-[26px] font-bold mt-1.5 tracking-tight">{stats.totalUsers}</div>
        </div>
        <div className="border border-line rounded-xl p-5 bg-white">
          <div className="text-xs text-muted uppercase tracking-wider font-semibold">Active connections</div>
          <div className="text-[26px] font-bold mt-1.5 tracking-tight text-green-700">{stats.activeConnections}</div>
        </div>
        <div className="border border-line rounded-xl p-5 bg-white">
          <div className="text-xs text-muted uppercase tracking-wider font-semibold">Expired connections</div>
          <div className="text-[26px] font-bold mt-1.5 tracking-tight text-red-700">{stats.expiredConnections}</div>
        </div>
        <div className="border border-line rounded-xl p-5 bg-white">
          <div className="text-xs text-muted uppercase tracking-wider font-semibold">Not connected</div>
          <div className="text-[26px] font-bold mt-1.5 tracking-tight">{stats.notConnected}</div>
        </div>
      </div>

      <div className="flex gap-3 items-center flex-wrap mb-5">
        <div className="flex-1 min-w-[240px] relative">
          <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-3.5-3.5" />
          </svg>
          <input
            type="text"
            placeholder="Search by name or email…"
            value={query}
            onChange={(e) => handleQueryChange(e.target.value)}
            className="w-full py-2.5 px-3.5 pl-10 border border-line rounded-lg text-sm bg-soft transition-all focus:outline-none focus:border-indigo focus:bg-white focus:ring-3 focus:ring-indigo/10"
          />
        </div>
        <div className="flex border border-line rounded-lg overflow-hidden bg-white">
          {FILTERS.map((f) => (
            <button
              key={f.value}
              className={`border-0 py-2.5 px-3.5 text-[13px] font-medium cursor-pointer border-r border-line last:border-r-0 transition-all ${
                filter === f.value ? "bg-[#1e293b] text-white" : "text-muted hover:bg-soft"
              }`}
              onClick={() => handleFilterChange(f.value)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="border border-line rounded-2xl overflow-hidden bg-white relative" style={{ display: "flex", flexDirection: "column" }}>
        <style>{`
          .table-scrollbar::-webkit-scrollbar {
            width: 6px;
          }
          .table-scrollbar::-webkit-scrollbar-track {
            background: #f1f5f9;
          }
          .table-scrollbar::-webkit-scrollbar-thumb {
            background: #cbd5e1;
            border-radius: 3px;
          }
          .table-scrollbar::-webkit-scrollbar-thumb:hover {
            background: #94a3b8;
          }
        `}</style>
        {loading ? (
          <div className="py-20 px-5">
            <div className="flex flex-col items-center justify-center text-center">
              <div className="relative">
                <div className="inline-block w-12 h-12 border-4 border-gray-200 border-t-indigo-600 rounded-full animate-spin"></div>
                <div
                  className="absolute top-0 left-0 inline-block w-12 h-12 border-4 border-transparent border-r-indigo-400 rounded-full animate-spin"
                  style={{ animationDirection: "reverse", animationDuration: "1s" }}
                ></div>
              </div>
              <p className="mt-4 text-sm font-medium text-gray-600">Loading users...</p>
              <p className="mt-1 text-xs text-gray-400">Please wait while we fetch the data</p>
            </div>
          </div>
        ) : error ? (
          <div className="py-16 px-5 text-center text-red-600">{error}</div>
        ) : (
          <>
            <div className="overflow-x-auto table-scrollbar">
              <table className="w-full border-collapse text-sm table-fixed">
                <colgroup>
                  <col style={{ width: "26%" }} />
                  <col style={{ width: "32%" }} />
                  <col style={{ width: "22%" }} />
                  <col style={{ width: "20%" }} />
                </colgroup>
                <thead className="sticky top-0 bg-soft z-10">
                  <tr>
                    <th className="text-left py-2.5 px-4 text-xs font-semibold text-muted uppercase tracking-wider border-b border-line">User</th>
                    <th className="text-left py-2.5 px-4 text-xs font-semibold text-muted uppercase tracking-wider border-b border-line">Connections</th>
                    <th className="text-left py-2.5 px-4 text-xs font-semibold text-muted uppercase tracking-wider border-b border-line">Realm ID</th>
                    <th className="text-left py-2.5 px-4 text-xs font-semibold text-muted uppercase tracking-wider border-b border-line">Created</th>
                  </tr>
                </thead>
                <tbody>{renderRows()}</tbody>
              </table>
            </div>

            <div
              className="sticky bottom-0 flex justify-between items-center py-3.5 px-5 border-t border-line z-10"
              style={{ background: "rgba(248, 250, 252, 0.95)", backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)" }}
            >
              <div className="text-[13px] text-muted font-medium">
                Page {pagination.page} of {pagination.totalPages || 1}
              </div>
              <div className="flex items-center gap-4">
                <select
                  value={pagination.limit}
                  onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
                  className="py-1.5 px-3 border border-line rounded-lg bg-white text-[13px] font-medium cursor-pointer hover:bg-soft transition-colors"
                >
                  <option value={10}>10 / page</option>
                  <option value={25}>25 / page</option>
                  <option value={50}>50 / page</option>
                  <option value={100}>100 / page</option>
                </select>
                <div className="flex gap-2">
                  <button
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page === 1}
                    className="py-2 px-3.5 border border-line rounded-lg bg-white text-[13px] font-medium cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:bg-soft transition-colors"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page === pagination.totalPages}
                    className="py-2 px-3.5 border border-line rounded-lg bg-white text-[13px] font-medium cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:bg-soft transition-colors"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default UsersPage;
